import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { environment } from 'src/environments/environment';
import { pushNotificationConfig_Register, pushNotificationConfig_Unregister } from 'src/app/GlobalVariables';
import { PushNotificationConfigDTO, PushNotificationConfigRegisterRequestDTO, PushNotificationConfigUnregisterRequestDTO } from './pushnotification.interface';
import { NotificationBellService } from 'src/app/Shared/notification-bell/notification-bell.service';
import { NotificationToastService } from 'src/app/Shared/notification-toast/notification-toast.service';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';

/**
 * Handles browser-side setup for Web Push (Firebase Cloud Messaging):
 *  - initializes the Firebase app (config comes from environment.firebase)
 *  - asks the user for notification permission
 *  - fetches the FCM device token, reusing the app's already-registered
 *    service worker (firebase-messaging-sw.js) rather than letting Firebase
 *    register a second, separate service worker of its own, so there is only
 *    ONE service worker for the whole app (see firebase-messaging-sw.js for
 *    the FCM background-message handling that lives in it, alongside the
 *    app's normal PWA/offline service worker duties)
 *  - registers that token against this user via /NT/PNC/Register
 *
 * NOTE: environment.firebase / environment.firebaseVapidKey are placeholders
 * until the real Firebase Web config + VAPID key are filled in
 * (src/environments/environment*.ts). Until then this service logs a
 * warning and no-ops instead of throwing, so the rest of login keeps working.
 *
 * Also owns wiring push arrivals straight into an immediate notification-bell refresh
 * (see wireLiveBellRefresh()) instead of leaving the bell to wait for its periodic poll --
 * one path for the tab in focus (Firebase's onMessage()), one for the tab open-but-not-focused
 * (a postMessage broadcast from firebase-messaging-sw.js's onBackgroundMessage). A fully
 * closed tab can't be reached either way and just picks the notification up on next poll.
 *
 * On top of that, wireCatchUpNotifications() drives two more toasts that aren't tied to a push
 * arriving right now: a "While You Were Away" summary when the tab regains visibility after
 * missing pushes while hidden, and a one-per-session "Pending Review" summary of whatever's
 * already unread at bootstrap.
 */
@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {

  private http = inject(HttpClient);
  private bellService = inject(NotificationBellService);
  private toastService = inject(NotificationToastService);
  private userLoginService = inject(UserLoginService);
  private firebaseApp: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private liveBellRefreshWired = false;
  private sessionRegistrationAttempted = false;
  // Cached so unregisterCurrentDevice() (called from UserLoginService.SignOutUser(), which clears
  // localStorage/the session synchronously right after) can fire its request immediately with
  // a known-good token, instead of awaiting another getToken() call that could race the session
  // teardown and lose its auth header.
  private lastKnownDeviceToken: string | null = null;

  // --- away catch-up + login summary toasts -----------------------------------------------
  // See wireCatchUpNotifications() below for what these drive. Kept separate from the
  // push/Firebase wiring above -- the login summary in particular has to work even when
  // Firebase isn't configured at all, since it's just reading the bell's own unread count.
  /** Guards the document.addEventListener() call only -- this is a page-lifetime concern (the
   *  listener itself is user-agnostic), so it is NEVER reset on sign-out. Kept separate from
   *  loginSummaryCheckStarted below, which DOES need to reset per login -- see that field's
   *  comment for why sharing one guard between the two was the actual bug behind "I logged in
   *  and didn't get the pending-notifications toast" on a second, same-tab login. */
  private visibilityListenerWired = false;
  /** Guards the one-shot unreadTotalCount$ subscription in checkLoginSummary() below. Reset to
   *  false in unregisterCurrentDevice() (sign-out) alongside the sessionStorage flag, so logging
   *  back in within the SAME browser tab (no full page reload -- PushNotificationService is a
   *  root singleton that survives that) re-arms the check instead of silently never running
   *  again for the rest of the tab's life. */
  private loginSummaryCheckStarted = false;
  /** How many ADVERP_PUSH_RECEIVED broadcasts (see wireLiveBellRefresh()) have landed since the
   *  tab last became visible. Reset to 0 every time visibility returns, whether or not that
   *  produced a toast. */
  private missedWhileHiddenCount = 0;
  /** Timestamp (Date.now()) the tab most recently became hidden, or null while visible. */
  private hiddenSince: number | null = null;
  /** Timestamp wireCatchUpNotifications() actually ran -- a visibilitychange within
   *  STARTUP_GRACE_MS of that is ignored, in case one fires spuriously during initial load. */
  private wiredAt = 0;

  private static readonly AWAY_THRESHOLD_MS = 60 * 1000;
  private static readonly STARTUP_GRACE_MS = 3000;
  private static readonly LOGIN_TOAST_SESSION_KEY = 'adverp.pendingNotificationsToastShown';

  PushNotificationConfig_Register(data: PushNotificationConfigRegisterRequestDTO): Observable<PushNotificationConfigDTO> {
    return this.http.post<PushNotificationConfigDTO>(pushNotificationConfig_Register, data);
  }

  PushNotificationConfig_Unregister(data: PushNotificationConfigUnregisterRequestDTO): Observable<PushNotificationConfigDTO> {
    return this.http.post<PushNotificationConfigDTO>(pushNotificationConfig_Unregister, data);
  }

  private isFirebaseConfigured(): boolean {
    const cfg = environment.firebase;
    return !!cfg
      && !!environment.firebaseVapidKey
      && !Object.values(cfg).some(v => !v || v.toString().startsWith('PUT_'))
      && !environment.firebaseVapidKey.startsWith('PUT_');
  }

  private ensureFirebaseInitialized(): Messaging | null {
    if (!this.isFirebaseConfigured()) {
      console.warn('[PushNotificationService] Firebase config is still a placeholder (see environment.ts) — skipping push registration.');
      return null;
    }
    if (!this.messaging) {
      this.firebaseApp = getApps().length ? getApps()[0] : initializeApp(environment.firebase as any);
      this.messaging = getMessaging(this.firebaseApp);
    }
    return this.messaging;
  }

  private detectBrowser(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome/') && !ua.includes('Chromium')) return 'Chrome';
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    return 'Unknown';
  }

  private detectOS(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'MacOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Unknown';
  }

  /**
   * Call this right after a user finishes logging in (once UserId, TenantId
   * and TenantBranchId are known). Safe to call every login — the backend SP
   * is keyed on (UserId, DeviceToken): if this exact pair is already
   * registered it's a no-op, otherwise a new row is inserted.
   */
  async registerCurrentDevice(userId: number, tenantId: number, tenantBranchId: number, actionUser: string): Promise<void> {
    try {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        console.warn('[PushNotificationService] Push notifications are not supported in this browser.');
        return;
      }

      const messaging = this.ensureFirebaseInitialized();
      if (!messaging) {
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn(`[PushNotificationService] Notification permission was "${permission}" — not registering a device token.`);
        return;
      }

      // Reuse the app's existing root service worker (firebase-messaging-sw.js) rather than
      // letting the Firebase SDK register a second service worker of its own.
      const swRegistration = await navigator.serviceWorker.ready;

      const token = await getToken(messaging, {
        vapidKey: environment.firebaseVapidKey,
        serviceWorkerRegistration: swRegistration
      });

      if (!token) {
        console.warn('[PushNotificationService] No FCM token was returned.');
        return;
      }

      this.lastKnownDeviceToken = token;
      this.wireLiveBellRefresh(messaging);

      const request: PushNotificationConfigRegisterRequestDTO = {
        tenantId: tenantId,
        tenantBranchId: tenantBranchId,
        userId: userId,
        deviceToken: token,
        deviceType: 'Web',
        browser: this.detectBrowser(),
        os: this.detectOS(),
        actionUser: actionUser
      };

      this.PushNotificationConfig_Register(request).subscribe({
        next: () => console.log('[PushNotificationService] Device registered for push notifications.'),
        error: (err) => console.error('[PushNotificationService] Failed to register device for push notifications:', err)
      });

    } catch (err) {
      console.error('[PushNotificationService] Error while setting up push notifications:', err);
    }
  }

  /**
   * (Re)establishes push registration + the live bell-refresh wiring for whichever user is
   * currently logged in, WITHOUT requiring a fresh login. registerCurrentDevice() historically
   * only ran from the login flow itself (login.component.ts) -- a page refresh on an
   * already-authenticated session (persisted JWT) never re-ran it, so a tab that's simply been
   * left open/refreshed across a code change (or across days) would never pick up push
   * registration or the onMessage/service-worker wiring at all. Call this from anywhere that
   * reliably mounts once per authenticated page load -- currently
   * NotificationBellComponent.ngOnInit(), the same place NotificationBellService.startPolling()
   * is bootstrapped from. Idempotent per page load (both bell instances call it).
   */
  registerCurrentDeviceFromSession(): void {
    if (this.sessionRegistrationAttempted) { return; }
    this.sessionRegistrationAttempted = true;

    const user = this.userLoginService.User();
    if (!user || !user.userId || !user.companyId || !user.loggedInBranchId) { return; }

    this.registerCurrentDevice(user.userId, user.companyId, user.loggedInBranchId, user.userId.toString());
  }

  /**
   * Called from UserLoginService.SignOutUser() right before it clears the session -- see the
   * discussion that led here. ann.PushNotificationConfig_Register keys a device row on
   * DeviceToken alone (one row per browser, strictly one UserId at a time), which is correct
   * going forward, but nothing used to run AT sign-out: a notification already queued for this
   * user (NotificationMaster.AlertConfigId pointing at this exact device row) would still
   * resolve and get delivered to whoever's using this same browser next. This deactivates the
   * row (ann.PushNotificationConfig_Unregister), and ann.GetPushNotifications now filters
   * IsActive/IsDeleted on that join too, so an already-queued notification fails cleanly
   * instead of leaking to the next person on this device.
   *
   * Best-effort and fire-and-forget by design -- sign-out must never be blocked or fail because
   * of this. Uses the token cached at registration time (lastKnownDeviceToken) rather than
   * calling getToken() again here, so the HTTP request fires synchronously (with the still-valid
   * auth header) instead of racing SignOutUser()'s immediate localStorage.clear().
   */
  unregisterCurrentDevice(): void {
    // Sign-out ends the "session" the login summary toast is scoped to -- next sign-in should
    // be able to show it again if there's still a backlog, and any pending away-catch-up state
    // clearly no longer applies to whoever logs in next on this device.
    sessionStorage.removeItem(PushNotificationService.LOGIN_TOAST_SESSION_KEY);
    this.missedWhileHiddenCount = 0;
    this.hiddenSince = null;
    this.loginSummaryCheckStarted = false;

    try {
      if (!this.lastKnownDeviceToken) {
        return;
      }

      const user = this.userLoginService.User();
      if (!user || !user.userId || !user.companyId || !user.loggedInBranchId) {
        return;
      }

      const request: PushNotificationConfigUnregisterRequestDTO = {
        tenantId: user.companyId,
        tenantBranchId: user.loggedInBranchId,
        userId: user.userId,
        deviceToken: this.lastKnownDeviceToken,
        actionUser: user.userId.toString()
      };

      this.PushNotificationConfig_Unregister(request).subscribe({
        next: () => console.log('[PushNotificationService] Device unregistered on sign-out.'),
        error: (err) => console.warn('[PushNotificationService] Failed to unregister device on sign-out:', err)
      });
    } catch (err) {
      console.warn('[PushNotificationService] Error while unregistering push device on sign-out:', err);
    }
  }

  /**
   * Makes a push notification refresh the bell the moment it arrives, instead of waiting for
   * NotificationBellService's periodic poll. Idempotent -- only wires up once per page load,
   * even though registerCurrentDevice() (its callers) can itself run more than once.
   */
  private wireLiveBellRefresh(messaging: Messaging): void {
    if (this.liveBellRefreshWired) { return; }
    this.liveBellRefreshWired = true;

    // Tab in focus -- Firebase delivers the push straight to the page via onMessage(), but
    // (unlike the background path below) it does NOT display anything on its own -- per
    // Firebase's docs, a foregrounded page is expected to decide for itself whether/how to show
    // something. See the in-app toast below (NotificationToastService) for what we show instead
    // of an OS-level Notification here.
    onMessage(messaging, (payload) => {
      console.log('[PushNotificationService] Foreground push received -- refreshing the notification bell.');
      this.bellService.refreshUnread();

      // Data-only payload (see FirebsaePushHandler.cs) -- title/body travel in payload.data,
      // not payload.notification, on purpose.
      //
      // While the tab is focused we show our OWN in-app toast (NotificationToastService) instead
      // of an OS-level Notification -- per Mihir's call: a browser notification popping up while
      // someone is actively looking at the app felt out of place, whereas a toast that slides in
      // from the right, stays ~5s and slides back out (aggregating into "N new notifications" if
      // several pushes land close together) reads as part of the software itself. The OS
      // notification is kept ONLY for the background/unfocused-tab path, which is handled
      // entirely by firebase-messaging-sw.js's onBackgroundMessage and is untouched by this.
      this.toastService.show({
        title: payload?.data?.['title'] || 'AdvERP',
        body: payload?.data?.['body'] || '',
        url: payload?.data?.['url'] || null
      });
    });

    // Tab open but not focused -- firebase-messaging-sw.js's onBackgroundMessage handled the
    // OS notification and broadcasts this so the open page can still refresh immediately.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'ADVERP_PUSH_RECEIVED') {
          console.log('[PushNotificationService] Background push received -- refreshing the notification bell.');
          this.bellService.refreshUnread();
          // Counted here rather than gated on document.hidden -- the SW only ever broadcasts
          // this for a push that took the onBackgroundMessage path in the first place, which by
          // definition means our own onMessage() above did NOT show a toast for it. Whether that
          // adds up to an actual "away" toast depends on how long the tab has been hidden when
          // it next becomes visible again -- see wireCatchUpNotifications() below.
          this.missedWhileHiddenCount++;
        }
      });
    }
  }

  /**
   * Two toasts that are NOT tied to a live push arriving right now:
   *
   *  - "While You Were Away": the tab was hidden for a while and one or more pushes landed
   *    during that time (counted via missedWhileHiddenCount above). Fires once, when the tab
   *    becomes visible again, IF it was hidden for at least AWAY_THRESHOLD_MS -- otherwise a
   *    quick alt-tab would pop this up for no reason.
   *  - "Pending Review": at app bootstrap, if the bell's own unread count is already > 0 (a
   *    backlog from before this page load), shown once per browser session (sessionStorage
   *    gated, cleared on sign-out in unregisterCurrentDevice()).
   *
   * These can't double up in practice: the login toast only ever fires once, synchronously
   * near page load, before any hide/show cycle could have happened; STARTUP_GRACE_MS is just a
   * defensive margin against a stray visibilitychange during that same initial load.
   *
   * Deliberately independent of push/Firebase being configured -- unlike registerCurrentDevice()
   * above, both toasts here are driven off NotificationBellService's own unread-count endpoint,
   * not FCM. Call from anywhere that reliably mounts once per authenticated page load (currently
   * NotificationBellComponent.ngOnInit(), alongside registerCurrentDeviceFromSession()).
   * Idempotent -- safe to call from both bell instances.
   */
  wireCatchUpNotifications(): void {
    if (!this.visibilityListenerWired) {
      this.visibilityListenerWired = true;
      this.wiredAt = Date.now();

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.hiddenSince = Date.now();
          return;
        }

        const wasHiddenSince = this.hiddenSince;
        this.hiddenSince = null;
        const missed = this.missedWhileHiddenCount;
        this.missedWhileHiddenCount = 0;

        if (wasHiddenSince === null || missed === 0) { return; }
        if (Date.now() - this.wiredAt < PushNotificationService.STARTUP_GRACE_MS) { return; }

        const awayMs = Date.now() - wasHiddenSince;
        if (awayMs >= PushNotificationService.AWAY_THRESHOLD_MS) {
          this.showAwayCatchUpToast(missed);
        }
      });
    }

    this.checkLoginSummary();
  }

  /**
   * One-shot per login: if the bell's unread count is already > 0 at bootstrap, shows the
   * "Pending Review" toast once. Split out from wireCatchUpNotifications() above specifically so
   * it can be re-armed independently of the (page-lifetime, never-reset) visibility listener --
   * see loginSummaryCheckStarted's field comment.
   */
  private checkLoginSummary(): void {
    if (this.loginSummaryCheckStarted) { return; }
    this.loginSummaryCheckStarted = true;

    // Skips the synchronous replay of whatever unreadTotalCount$ (a BehaviorSubject) already
    // holds -- at this point in the app's lifecycle that's always still its seed value of 0,
    // since NotificationBellComponent calls bellService.startPolling() (which kicks off the
    // real HTTP request) immediately before this method, and that request can't have resolved
    // yet. The real count arrives as this subscription's SECOND emission.
    let sawInitialValue = false;
    const loginCheckSub = this.bellService.unreadTotalCount$.subscribe(count => {
      if (!sawInitialValue) {
        sawInitialValue = true;
        return;
      }
      loginCheckSub.unsubscribe();
      if (count > 0 && !sessionStorage.getItem(PushNotificationService.LOGIN_TOAST_SESSION_KEY)) {
        sessionStorage.setItem(PushNotificationService.LOGIN_TOAST_SESSION_KEY, '1');
        this.showLoginSummaryToast(count);
      }
    });
  }

  private showAwayCatchUpToast(missedCount: number): void {
    let latestSubject: string | undefined;
    const sub = this.bellService.unread$.subscribe(items => { latestSubject = items[0]?.subject; });
    sub.unsubscribe();

    this.toastService.show({
      title: `${missedCount} notification${missedCount > 1 ? 's' : ''} arrived`,
      body: latestSubject ? `Latest: ${latestSubject}` : 'Tap the bell to view all',
      url: null
    }, 'away');
  }

  private showLoginSummaryToast(count: number): void {
    this.toastService.show({
      title: `${count} notification${count > 1 ? 's' : ''} need your attention`,
      body: 'Tap the bell to review',
      url: null
    }, 'login');
  }
}
